import { get, patch, post, put } from "../utils/FetchV2.util";
import type { SupportIssue } from "../../@types/Profile.type";
import { getUtmsForProfile } from "../attribution/utm-manager";
import {
  buildUtmsExtra,
  WEBAPP_ACQUISITION_CHANNEL_ID,
} from "../attribution/acquisitionProfile";
import { getAnonymousId } from "../utils/anonymousId";
import { getGA4ClientId } from "../analytics/ga4";
import { getIsNative, getPlatformOs } from "../utils/general.util";

const resolveOtpPlatform = (): string => {
  if (getIsNative()) {
    const platformOs = getPlatformOs();
    if (platformOs) return platformOs;
  }
  return "web";
};

export const sendOTPService = async ({
  cellphone,
  countryCode,
  send_by = "sms",
  platform,
  fallback = false,
}: {
  cellphone: string;
  countryCode: string;
  send_by?: string;
  platform?: string;
  fallback?: boolean;
}): Promise<any> => {
  try {
    const disableSentOtp = import.meta.env.VITE_DISABLE_SENT_OTP;
    const resolvedPlatform = platform ?? resolveOtpPlatform();

    const response = await post({
      service: "userManagementEngine",
      endpoint: `/user-account/send-otp?platform=${resolvedPlatform}`,
      body: {
        send_by,
        cellphone,
        country_code: `+${countryCode}`,
        heru_pal: disableSentOtp === "true",
        fallback,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const verifyOTPService = async ({
  cellphone,
  code,
  countryCode,
  skipAccountError = false,
}: {
  cellphone: string;
  code: string;
  countryCode: string;
  skipAccountError?: boolean;
}): Promise<any> => {
  try {
    const response = await post({
      service: "userManagementEngine",
      endpoint: `/user-account/verify-otp`,
      body: {
        cellphone,
        code,
        country_code: `+${countryCode}`,
        skip_account_error: skipAccountError,
      },
      skipAccountError,
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const sendVerifyOtpService = async ({
  cellphone,
  countryCode,
  sendBy,
}: {
  cellphone: string;
  countryCode: string;
  sendBy?: string;
}): Promise<any> => {
  try {
    const response = await post({
      service: "userManagementEngine",
      endpoint: "/user-account/web/send-verify-otp",
      body: {
        cellphone,
        country_code: `+${countryCode}`,
        ...(sendBy && { send_by: sendBy }),
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const saveCellphoneService = async ({
  cellphone,
  countryCode,
}: {
  cellphone: string;
  countryCode: string;
}): Promise<any> => {
  const response = await post({
    service: "userManagementEngine",
    endpoint: "/user-account/web/save-cellphone",
    body: {
      cellphone,
      country_code: `+${countryCode}`,
    },
  });

  return response;
};

export const verifyCellphoneService = async ({
  cellphone,
  countryCode,
  otpCode,
}: {
  cellphone: string;
  countryCode: string;
  otpCode: string;
}): Promise<any> => {
  try {
    const response = await post({
      service: "userManagementEngine",
      endpoint: "/user-account/web/verify-cellphone",
      body: {
        cellphone,
        country_code: `+${countryCode}`,
        otp_code: otpCode,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const sendMergeOtpService = async ({
  cellphone,
  countryCode,
  sendBy,
}: {
  cellphone: string;
  countryCode: string;
  sendBy?: string;
}): Promise<any> => {
  const response = await post({
    service: "userManagementEngine",
    endpoint: "/user-account/web/send-merge-otp",
    body: {
      cellphone,
      country_code: `+${countryCode}`,
      ...(sendBy && { send_by: sendBy }),
    },
  });

  return response;
};

export const mergeAccountByCellphoneService = async ({
  cellphone,
  countryCode,
  otpCode,
}: {
  cellphone: string;
  countryCode: string;
  otpCode: string;
}): Promise<any> => {
  const response = await post({
    service: "userManagementEngine",
    endpoint: "/user-account/web/merge-by-cellphone",
    body: {
      cellphone,
      country_code: `+${countryCode}`,
      otp_code: otpCode,
    },
  });

  return response;
};

export const sendEmailMergeOtpService = async ({
  email,
}: {
  email: string;
}): Promise<any> => {
  const response = await post({
    service: "userManagementEngine",
    endpoint: "/user-account/web/send-email-merge-otp",
    body: { email },
  });

  return response;
};

export const mergeAccountByEmailService = async ({
  email,
  code,
}: {
  email: string;
  code: string;
}): Promise<any> => {
  const response = await post({
    service: "userManagementEngine",
    endpoint: "/user-account/web/merge-by-email",
    body: { email, code },
  });

  return response;
};

/**
 * Verificación del correo del usuario YA AUTENTICADO — gemelo exacto del par
 * del teléfono (`web/send-verify-otp` + `web/verify-cellphone`), y por eso vive
 * bajo el mismo prefijo `web/`.
 *
 * NO confundir con `sendEmailOtpService`/`verifyEmailOtpService` de abajo: ESOS
 * son login por correo y su verify DEVUELVE TOKENS — con un correo sin cuenta
 * crean una cuenta nueva. Llamarlos desde el onboarding le cambiaría la sesión
 * al usuario y lo dejaría en una cuenta vacía: exactamente la duplicación que el
 * nodo de verificación existe para matar. Estos estampan sobre la sesión
 * vigente y no emiten tokens.
 */
export const sendVerifyEmailOtpService = async ({
  email,
}: {
  email: string;
}): Promise<any> => {
  const response = await post({
    service: "userManagementEngine",
    endpoint: "/user-account/web/send-verify-email-otp",
    body: { email },
  });

  return response;
};

export const verifyEmailService = async ({
  email,
  otpCode,
}: {
  email: string;
  otpCode: string;
}): Promise<any> => {
  const response = await post({
    service: "userManagementEngine",
    endpoint: "/user-account/web/verify-email",
    body: { email, otp_code: otpCode },
  });

  return response;
};

export const sendEmailOtpService = async ({
  email,
}: {
  email: string;
}): Promise<any> => {
  try {
    const response = await post({
      service: "userManagementEngine",
      endpoint: "/user-account/send-email-otp",
      body: { email },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const verifyEmailOtpService = async ({
  email,
  code,
}: {
  email: string;
  code: string;
}): Promise<any> => {
  try {
    const response = await post({
      service: "userManagementEngine",
      endpoint: "/user-account/verify-email-otp",
      body: { email, code },
      skipAccountError: true,
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getUserProfileService = async (): Promise<any> => {
  try {
    const response = await get({
      service: "userManagementEngine",
      endpoint: "/user-account/user-profile",
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export interface UpdateUserProfilePayload {
  first_name?: string;
  middle_name?: string;
  father_surname?: string;
  mother_surname?: string;
  email?: string;
  utm_campaign?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string | null;
  utm_content?: string | null;
  utms_extra?: Record<string, string>;
  acquisition_channel_id?: number;
  anonymous_id?: string;
}

/** Contrato de error del update-user: cuando el correo enviado ya pertenece a
 * otra cuenta, el backend responde este texto exacto. El engine lo manda como
 * problem-details (`detail`, status 400); el monolito legacy lo mandaba en
 * `message` — el detector cubre ambas llaves. Único detector — lo consumen el
 * EmailStep v2 y el gate de correo del onboarding v3. */
const EMAIL_ALREADY_REGISTERED_FRAGMENT =
  'El correo electrónico ya está registrado';

const backendErrorText = (error: unknown): string => {
  const data = (
    error as { response?: { data?: { detail?: string; message?: string } } }
  )?.response?.data;
  return data?.detail ?? data?.message ?? '';
};

export const isEmailAlreadyRegisteredError = (error: unknown): boolean =>
  backendErrorText(error).includes(EMAIL_ALREADY_REGISTERED_FRAGMENT);

/** Contrato de error del send-verify-otp: cuando el celular enviado ya pertenece
 * a otra cuenta, el backend responde este texto exacto con status 400. Es la
 * puerta al merge por teléfono (send-merge-otp + merge-by-cellphone), igual que
 * su gemelo de correo — no un fallo de envío. */
const PHONE_ALREADY_REGISTERED_FRAGMENT =
  'Este número de teléfono ya está registrado';

export const isPhoneAlreadyRegisteredError = (error: unknown): boolean =>
  backendErrorText(error).includes(PHONE_ALREADY_REGISTERED_FRAGMENT);

export const patchUserProfileService = async ({
  body,
}: {
  body: UpdateUserProfilePayload;
}): Promise<any> => {
  try {
    const response = await patch({
      // UPDATE migrado al engine. El update-user del engine es parcial (addField:
      // solo escribe los campos enviados) y persiste utms_extra (click IDs).
      // REQUISITO: el engine deployado debe ser un build de `main` (no el binario
      // full-replace viejo que nuleaba cellphone/country_code en updates parciales).
      // Verificar con el integration test TestUpdateUser_PartialDoesNotClobber
      // y/o el probe de paridad antes de confiar en prod.
      service: "userManagementEngine",
      endpoint: "/user-account/web/update-user",
      body,
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export interface DeviceInfo {
  platform?: string;
  device_id?: string;
  ip_address?: string;
  user_agent?: string;
}

export const loginWithGoogleService = async (
  credential: { code?: string; idToken?: string }
) => {
  try {
    const utms = getUtmsForProfile();
    const ga4ClientId = await getGA4ClientId();
    // Los click IDs viajan en el ALTA, no en un PATCH posterior. El alta con
    // Google llega con correo, y el PATCH de adquisición está detrás de "usuario
    // sin correo" — así que por este camino ningún click id llegaba nunca al
    // perfil, ni el de afiliado (con el que se factura la comisión) ni un gclid.
    // El backend los escribe solo al CREAR la cuenta, de modo que un login
    // posterior no puede reescribirle la atribución a un usuario que ya existía.
    // Por la misma razón (no hay PATCH posterior en este camino), el canal de
    // adquisición y el anonymous_id del stitch de touchpoints viajan aquí: son
    // los mismos dos campos que `buildAcquisitionProfilePatch` manda en el OTP.
    const utmsExtra = buildUtmsExtra();
    const response = await post({
      service: "userManagementEngine",
      endpoint: "/auth/google/login",
      body: {
        ...(credential.code ? { code: credential.code } : {}),
        ...(credential.idToken ? { id_token: credential.idToken } : {}),
        utm_source: utms.utm_source,
        utm_medium: utms.utm_medium,
        utm_campaign: utms.utm_campaign,
        ga4_client_id: ga4ClientId,
        acquisition_channel_id: WEBAPP_ACQUISITION_CHANNEL_ID,
        anonymous_id: getAnonymousId(),
        ...(Object.keys(utmsExtra).length > 0 && { utms_extra: utmsExtra }),
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const refreshGoogleTokenService = async (refreshToken: string) => {
  try {
    const response = await post({
      service: "userManagementEngine",
      endpoint: "/auth/google/refresh",
      body: {
        refresh_token: refreshToken,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Vincula una identidad de Google a la sesión VIGENTE y estampa su correo ya
 * verificado. FetchV2 manda el Bearer del usuario actual, así que el backend
 * sabe a quién vincular — a diferencia de `/auth/google/login`, que abre sesión
 * como la identidad de Google y con un correo sin cuenta CREA una cuenta nueva.
 * Ese es el camino preferente del nodo de verificación de correo (88% de los
 * usuarios son gmail): un tap, sin código.
 *
 * Recibe el `code` del flujo auth-code, único que acepta el endpoint
 * (`GoogleLinkInput.code`, required): mandarle un `id_token` responde 422.
 */
export const linkGoogleAccountService = async (code: string) => {
  const response = await post({
    service: "userManagementEngine",
    endpoint: "/auth/google/link",
    body: { code },
  });

  return response;
};

/**
 * Siempre resuelve a un array: el engine responde 200 con body vacío cuando el
 * usuario no tiene issues y FetchV2 lo entrega como `undefined` — los consumers
 * iteran el resultado directo, así que la normalización vive aquí.
 */
export const getSupportIssuesService = async (): Promise<SupportIssue[]> => {
  try {
    const response = await get({
      service: "userManagementEngine",
      endpoint: "/user-account/web/support-issues",
    });

    return Array.isArray(response) ? response : [];
  } catch (error) {
    throw error;
  }
};

export const submitSupportIssueService = async (body: any) => {
  try {
    const response = await post({
      // Engine deriva el user del JWT y valida estricto; user_id extra da 422.
      service: "userManagementEngine",
      endpoint: "/user-account/web/support-issue",
      body: {
        category_type: body.category_type,
        category_detail: body.category_detail,
        message: body.message,
        escalation_type: body.escalation_type,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

// Catálogo de alianzas migrado a sales-transaction-engine (el tier es la unidad
// de descuento, así que es dominio de ventas). Mismo shape que el
// `/organizations/web/all` de user-management, con una sola diferencia
// intencional: solo devuelve organizaciones activas, así que los partners
// dados de baja dejan de llegar al selector y al flujo de aliados.
export const fetchAllOrganizationsService = async () => {
  try {
    const response = await get({
      service: "salesTransactionEngine",
      endpoint: "/organizations",
    });

    return response;
  } catch (error) {
    throw error;
  }
};

// Vinculación self-service migrada a sales-transaction-engine: el sujeto sale
// del JWT, por eso la ruta no lleva user_id. Idempotente; 404 si el tier no
// existe. Reemplaza el `/organizations/web/assign-tier/users/tier/{tier}` de
// user-management.
export const postAssignOrganizationService = async ({
  tierId,
}: {
  tierId: number;
}) => {
  try {
    const response = await post({
      service: "salesTransactionEngine",
      endpoint: `/organizations/me/tiers/${tierId}`,
      body: {},
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteAccountService = async () => {
  try {
    const response = await put({
      service: "userManagementEngine",
      endpoint: `/user-account/web/deactivate-user`,
      body: {},
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const attributeUserService = async (body: {
  channel: string;
  channel_drilldown1?: string;
  channel_drilldown2?: string;
  channel_drilldown3?: string;
  channel_drilldown4?: string;
  landing_page_group?: string;
  landing_page?: string;
}) => {
  try {
    const response = await post({
      // El endpoint web del engine deriva el user del JWT y valida estricto
      // (rechaza props extra), así que aquí no viaja ningún user_id: sería
      // redundante y dispara un 422.
      service: "userManagementEngine",
      endpoint: "/user-account/web/attribution",
      body: {
        channel: body.channel,
        channel_drilldown1: body.channel_drilldown1,
        channel_drilldown2: body.channel_drilldown2,
        channel_drilldown3: body.channel_drilldown3,
        channel_drilldown4: body.channel_drilldown4,
        landing_page: body.landing_page,
        landing_page_group: body.landing_page_group,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const acceptComplianceService = async ({
  doc_type,
  version,
}: {
  doc_type: string;
  version?: string;
}) => {
  try {
    const response = await post({
      // Migrado al engine: el web/compliance del Node devolvía 500; el del engine
      // (compliance.webCreateLog) acepta {doc_type, version?} y crea el log bien
      // (verificado e2e contra prod). doc_type required, version opcional.
      service: "userManagementEngine",
      endpoint: `/user-account/web/compliance`,
      body: {
        doc_type,
        version,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};
